import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { Subscription } from 'rxjs';
import { AssetHeaderConfig } from '../../shared/components/asset-header/asset-header.component';
import { AssetHeaderComponent } from '../../shared/components/asset-header/asset-header.component';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { FilterConfig } from '../../shared/components/asset-filter/asset-filter.component';
import { UsecaseCardData } from '../../shared/components/usecase-card/usecase-card.component';
import { AssetListingPageComponent } from '../../shared/components/asset-listing-page/asset-listing-page.component';
import { LoginModalComponent } from '../../shared/components/login-modal/login-modal.component';
import { ModalComponent } from '../../shared/components/primeng/modal/modal.component';
// Share feature: commented out for now. Uncomment to re-enable.
// import { CardShareButtonComponent } from '../../shared/components/card-share-button/card-share-button.component';
import { InViewDirective } from '../../shared/directives/in-view.directive';
import { OverflowScrollDirective } from '../../shared/directives/overflow-scroll.directive';
import { CatalogueApiService, SearchCriteria } from '@angular-workspace/services';
import { FormatService } from '../../shared/services/format.service';
import { HelperService } from '../../shared/services/helper.service';
import { USECASES_JSON, USECASE_ICON_FILENAMES, UsecaseJsonItem } from './usecases-data';

/** Icon key for use-case card (matches Applicable Use-Cases document). */
export type UsecaseIconKey = 'seed' | 'pest' | 'pest-disease' | 'fertiliser' | 'harvest' | 'logistics' | 'default';

/** Card data for the new AI Application design (no detail page). */
export interface AiApplicationCard {
  id: string;
  title: string;
  iconKey: UsecaseIconKey;
  /** When set, use this path for icon img (e.g. from static JSON). */
  iconPath?: string;
  datasetsRequired: string;
  howItCreatesValue: string;
  primaryBeneficiaries: string;
}

@Component({
  selector: 'app-usecases',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    LoginModalComponent,
    ModalComponent,
    // CardShareButtonComponent, // Share feature: uncomment to re-enable
    InViewDirective,
    OverflowScrollDirective
  ],
  templateUrl: './usecases.component.html',
  styleUrl: './usecases.component.scss',
})
export class UsecasesComponent implements OnInit, OnDestroy {
  isLoggedIn: boolean = false;
  showLoginModal: boolean = false;

  private keycloak = inject(KeycloakService);
  private keycloakSubscription?: Subscription;

  constructor(
    private catalogueApiService: CatalogueApiService,
    private formatService: FormatService,
    private helperService: HelperService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) { }
  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Use Cases' }
    ],
    title: 'Use Cases',
    description: 'Access the use case library to inspire your next innovation.'
    // iconPath removed - not needed as per design
  };

  filterConfig: FilterConfig = {
    sections: [
      {
        id: 'organisation',
        title: 'Organisation',
        type: 'checkbox',
        isExpanded: false,
        icon: '/filters/building-2.svg',
        options: []
      },
      {
        id: 'organisation-type',
        title: 'Organisation type',
        type: 'checkbox',
        isExpanded: false,
        icon: '/filters/people.svg',
        options: []
      },
      {
        id: 'last-updated',
        title: 'Last Updated At',
        type: 'radio',
        isExpanded: false,
        icon: '/filters/calendar.svg',
        options: [
          { id: '1-day', label: '1 Day ago', checked: false },
          { id: '1-week', label: '1 week ago', checked: false },
          { id: '1-month', label: '1 month ago', checked: false },
          { id: '1-year', label: '1 year ago', checked: false }
        ]
      }
    ],
    activeFilterCount: 0
  };

  /** New design: AI Application cards (no detail page). */
  aiApplications: AiApplicationCard[] = [];
  loading = false;

  /** Datasets "View more" modal */
  datasetsModalVisible = false;
  datasetsModalTitle = '';
  datasetsModalItems: string[] = [];
  readonly datasetsVisibleLimit = 4;

  openDatasetsModal(title: string, items: string[]): void {
    this.datasetsModalTitle = title;
    this.datasetsModalItems = [...items];
    this.datasetsModalVisible = true;
  }

  /** Kept for commented-out catalogue: filter + grid. */
  usecases: UsecaseCardData[] = [];
  currentPage = 1;
  totalPages = 0;
  viewMode: 'grid' | 'list' | 'table' = 'grid';
  itemsPerPage = 6;
  searchQuery: string = '';
  sortOption: string = 'new-to-old';
  isMobileFilterOpen = false;

  async ngOnInit(): Promise<void> {
    // Check login status
    await this.checkLoginStatus();

    // Subscribe to Keycloak events to update login status dynamically
    this.keycloakSubscription = this.keycloak.keycloakEvents$.subscribe(
      async (event: unknown) => {
        const keycloakEvent = event as { type: KeycloakEventType };
        if (keycloakEvent.type === KeycloakEventType.OnAuthSuccess) {
          this.isLoggedIn = true;
          console.log('User logged in, updating login status');
        } else if (
          keycloakEvent.type === KeycloakEventType.OnAuthRefreshSuccess ||
          keycloakEvent.type === KeycloakEventType.OnAuthLogout
        ) {
          await this.checkLoginStatus();
        }
      }
    );

    // Check for search query from route parameters
    this.route.queryParams.subscribe(params => {
      if (params['search']) {
        this.searchQuery = params['search'];
      }
    });

    this.loadUsecases();
    this.loadFilterOptions(); // Load filter options in parallel
    this.updateActiveFilterCount();
  }

  ngOnDestroy(): void {
    if (this.keycloakSubscription) {
      this.keycloakSubscription.unsubscribe();
    }
  }

  async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      this.isLoggedIn = false;
    }
  }

  onBookmarksClick(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
    }
  }

  onLikeClick(): void {
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
    }
  }

  async loadUsecases(): Promise<void> {
    this.loading = true;
    try {
      this.aiApplications = USECASES_JSON.map(item => this.mapJsonItemToAiApplicationCard(item));
      this.cdr.detectChanges();
      this.scrollToSharedUsecase();
    } catch (error) {
      console.error('Failed to load usecases:', error);
      this.aiApplications = [];
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  /** Land on a shared AI-app card when URL has `#usecase-{id}`. */
  private scrollToSharedUsecase(): void {
    const fragment =
      this.route.snapshot.fragment ||
      (typeof window !== 'undefined'
        ? window.location.hash.replace(/^#/, '')
        : '');
    if (!fragment?.startsWith('usecase-')) {
      return;
    }
    requestAnimationFrame(() => {
      document.getElementById(fragment)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }

  /** Map static JSON item to AI Application card; icon from public/usecase folder. */
  private mapJsonItemToAiApplicationCard(item: UsecaseJsonItem): AiApplicationCard {
    const iconKey = this.getIconKeyFromJsonIcon(item.icon);
    const filename = USECASE_ICON_FILENAMES[item.icon] ?? item.icon;
    const iconPath = `/usecase/${filename}.png`;
    return {
      id: item.id,
      title: item.title,
      iconKey,
      iconPath,
      datasetsRequired: item.datasets_required?.join(', ') ?? '—',
      howItCreatesValue: item.how_it_creates_value ?? '—',
      primaryBeneficiaries: item.primary_beneficiaries?.join(', ') ?? '—'
    };
  }

  private getIconKeyFromJsonIcon(icon: string): UsecaseIconKey {
    const map: Record<string, UsecaseIconKey> = {
      plant: 'seed',
      bug: 'pest',
      disease_control: 'pest-disease',
      fertilizer: 'fertiliser',
      fertiliser: 'fertiliser',
      harvest: 'harvest',
      logistics: 'logistics'
    };
    return map[icon] ?? 'default';
  }

  /** Map title/description keywords to icon key (Applicable Use-Cases document). */
  getIconKeyForTitle(title: string, description: string): UsecaseIconKey {
    const t = (title + ' ' + description).toLowerCase();
    if (t.includes('seed') && (t.includes('variety') || t.includes('performance'))) return 'seed';
    if (t.includes('pest') && (t.includes('outbreak') || t.includes('prediction'))) return 'pest';
    if (t.includes('pest') && (t.includes('disease') || t.includes('identification') || t.includes('control'))) return 'pest-disease';
    if (t.includes('fertiliser') || t.includes('fertilizer') || t.includes('blend')) return 'fertiliser';
    if (t.includes('harvest') || t.includes('maturity') || t.includes('sugarcane') || t.includes('horticultural')) return 'harvest';
    if (t.includes('logistics') || t.includes('farm gate') || t.includes('transport') || t.includes('route')) return 'logistics';
    return 'default';
  }

  /** Map API item to new AI Application card (no detail page). */
  private mapApiItemToAiApplicationCard(item: any): AiApplicationCard {
    const id = item.id || item._id || item.itemId || '';
    const title = item.label || item.name || 'Untitled';
    const description = item.description || item.summary || 'No description available.';
    const tags = Array.isArray(item.tags) ? item.tags : [];
    const datasetsRequired = item.datasetsRequired ?? item.datasets ?? (tags.length ? tags.join(', ') : '—');
    const howItCreatesValue = description;
    const primaryBeneficiaries = item.primaryBeneficiaries ?? item.beneficiaries ?? '—';
    const iconKey = this.getIconKeyForTitle(title, description);
    return {
      id,
      title,
      iconKey,
      datasetsRequired: typeof datasetsRequired === 'string' ? datasetsRequired : (Array.isArray(datasetsRequired) ? datasetsRequired.join(', ') : '—'),
      howItCreatesValue,
      primaryBeneficiaries: typeof primaryBeneficiaries === 'string' ? primaryBeneficiaries : (Array.isArray(primaryBeneficiaries) ? primaryBeneficiaries.join(', ') : '—')
    };
  }

  private usecaseCardToAiApplicationCard(u: UsecaseCardData): AiApplicationCard {
    return {
      id: u.id,
      title: u.title,
      iconKey: this.getIconKeyForTitle(u.title, u.description || ''),
      datasetsRequired: u.tags?.length ? u.tags.join(', ') : '—',
      howItCreatesValue: u.description || '—',
      primaryBeneficiaries: '—'
    };
  }

  trackById(_index: number, app: AiApplicationCard): string {
    return app.id;
  }

  /** Parse comma-separated string into array for chips; empty or "—" returns []. */
  getChips(value: string): string[] {
    if (!value || value.trim() === '—') return [];
    return value.split(',').map(s => s.trim()).filter(Boolean);
  }

  /**
   * Load filter options from API
   */
  private async loadFilterOptions(): Promise<void> {
    try {
      // Build searchCriteria for filter list
      const searchCriteria: SearchCriteria[] = [
        {
          searchType: 'term',
          field: 'type',
          values: ['adex:Apps']
        }
      ];

      // Define the filter fields to retrieve
      const filterFields = [
        'department',
        'organizationType'
      ];

      // Call the API to get filter options
      const response = await this.catalogueApiService.getFilterList(searchCriteria, filterFields);

      // Map the response to populate filter sections
      if (response?.result) {
        // If result is an array with objects, use the first object (which contains all filter arrays)
        if (Array.isArray(response.result) && response.result.length > 0) {
          this.populateFilterSections(response.result[0]);
        } else if (typeof response.result === 'object' && !Array.isArray(response.result)) {
          // If result is a single object
          this.populateFilterSections(response.result);
        }
      }
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  }

  /**
   * Populate filter sections with data from API response
   */
  private populateFilterSections(apiData: any): void {
    // Helper function to extract unique values from array of objects or use array directly
    const extractValues = (data: any, field: string): string[] => {
      if (!data) return [];

      // If it's already an array of strings, return unique values
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
        return [...new Set(data.filter((v: any) => v !== null && v !== undefined && v !== ''))];
      }

      // If it's an array of objects, extract unique values from the field
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
        const values: string[] = [];
        data.forEach((item: any) => {
          // Handle nested fields
          const fieldParts = field.split('.');
          let value = item;
          for (const part of fieldParts) {
            value = value?.[part];
          }

          // Handle array values (e.g., tags might be arrays)
          if (Array.isArray(value)) {
            values.push(...value.filter((v: any) => v !== null && v !== undefined && v !== ''));
          } else if (value !== null && value !== undefined && value !== '') {
            values.push(String(value));
          }
        });
        return [...new Set(values)];
      }

      // If it's a single object with the field
      if (typeof data === 'object' && !Array.isArray(data)) {
        const fieldParts = field.split('.');
        let value = data;
        for (const part of fieldParts) {
          value = value?.[part];
        }

        if (Array.isArray(value)) {
          return [...new Set(value.filter((v: any) => v !== null && v !== undefined && v !== ''))];
        } else if (value !== null && value !== undefined && value !== '') {
          return [String(value)];
        }
      }

      return [];
    };

    // Helper function to populate a filter section
    const populateSection = (sectionId: string, values: string[]) => {
      if (values.length === 0) return;

      const section = this.filterConfig.sections.find(s => s.id === sectionId);
      if (section) {
        section.options = values.map((value: string, index: number) => ({
          id: `${sectionId}-${index}`,
          label: value,
          checked: false
        }));
      }
    };

    // Extract and populate department -> organisation
    const departments = extractValues(apiData, 'department');
    populateSection('organisation', departments);

    // Extract and populate organizationType -> organisation-type
    const orgTypes = extractValues(apiData, 'organizationType');
    populateSection('organisation-type', orgTypes);

    // Update active filter count after populating
    this.updateActiveFilterCount();
  }

  /**
   * Extract items array from API response
   */
  private extractItemsFromResponse(response: any): any[] {
    // Handle null/undefined response
    if (!response) {
      console.warn('Response is null or undefined');
      return [];
    }

    // Direct array response
    if (Array.isArray(response)) {
      return response;
    }

    // Check for result array (most common structure: { result: [...], paginationInfo: {...} })
    if (response.result) {
      if (Array.isArray(response.result)) {
        return response.result;
      }
      // Handle nested result.results
      if (response.result.results && Array.isArray(response.result.results)) {
        return response.result.results;
      }
    }

    // Check for data array
    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }

    // Check for items array
    if (response.items && Array.isArray(response.items)) {
      return response.items;
    }

    // Log warning if no items found
    console.warn('No items array found in response. Response structure:', Object.keys(response));
    return [];
  }

  /**
   * Map API item to UsecaseCardData format
   */
  private mapApiItemToUsecaseCard(item: any): UsecaseCardData {
    // Extract basic fields - try multiple possible ID fields
    const id = item.id || item._id || item.itemId || item.usecaseId || '';
    if (!id) {
      console.warn('Usecase item missing ID:', item);
    }
    const title = item.label || item.name || 'Untitled';
    const description = item.description || item.summary || 'No description available';

    // Extract tags from API
    const apiTags: string[] = [];
    if (item.tags && Array.isArray(item.tags)) {
      apiTags.push(...item.tags);
    }

    // Extract badge/status using helper service
    const badge = this.helperService.extractAccessBadge(item);
    const badgeColor = this.helperService.getBadgeColorVariant(badge);

    // Check if item is new (created within last 7 days)
    const isNew = this.isRecentItem(item.itemCreatedAt || item.createdAt);

    // Build link based on asset type
    const link = `/usecase/${id}`;

    // Extract metrics data from metrics object - always include metrics object
    const metricsData = item.metrics || {};
    const metrics = {
      likes: metricsData.likes ?? item.likeCount ?? item.likes ?? item.like_count ?? 0,
      downloads: metricsData.downloads ?? item.downloadCount ?? item.downloads ?? item.download_count ?? 0,
      files: item.fileCount || item.files || item.file_count || 0,
      views: metricsData.views ?? item.viewCount ?? item.views ?? item.view_count ?? 0,
      dislikes: metricsData.dislikes ?? 0
    };

    return {
      id,
      title,
      description,
      tags: apiTags.slice(0, 4), // Show up to 4 tags for usecases
      apiTags: apiTags, // Store all tags for table view expansion
      badge,
      badgeColor,
      isNew,
      link,
      metrics: metrics // Always include metrics object
    };
  }

  /**
   * Check if item was created within last 7 days
   */
  private isRecentItem(dateString: string | Date): boolean {
    if (!dateString) return false;
    const itemDate = new Date(dateString);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return itemDate >= weekAgo;
  }

  /**
   * Build searchCriteria array from selected filters
   */
  private buildSearchCriteriaFromFilters(): SearchCriteria[] {
    const searchCriteria: SearchCriteria[] = [];

    // Process each filter section
    this.filterConfig.sections.forEach(section => {
      if (!section.options || section.options.length === 0) {
        return;
      }

      // Get checked options
      const checkedOptions = section.options.filter(opt => opt.checked);
      if (checkedOptions.length === 0) {
        return;
      }

      const values = checkedOptions.map(opt => opt.label);

      // Map section IDs to API field names and search types
      switch (section.id) {
        case 'organisation':
          searchCriteria.push({
            searchType: 'term',
            field: 'department',
            values: values
          });
          break;

        case 'organisation-type':
          searchCriteria.push({
            searchType: 'term',
            field: 'organizationType',
            values: values
          });
          break;

        case 'last-updated':
          // Handle date range for last updated
          const dateRange = this.getDateRangeFromLastUpdated(checkedOptions);
          if (dateRange) {
            searchCriteria.push({
              searchType: 'betweenTemporal',
              field: 'lastUpdated',
              values: [dateRange.start, dateRange.end]
            });
          }
          break;
      }
    });

    return searchCriteria;
  }

  /**
   * Get date range from last updated filter options
   */
  private getDateRangeFromLastUpdated(checkedOptions: any[]): { start: string; end: string } | null {
    if (checkedOptions.length === 0) {
      return null;
    }

    // Get the most recent date range (if multiple selected, use the most recent)
    const now = new Date();
    let startDate: Date | null = null;
    const endDate = new Date(now);

    // Find the most recent date range
    checkedOptions.forEach(option => {
      let daysAgo = 0;
      switch (option.id) {
        case '1-day':
          daysAgo = 1;
          break;
        case '1-week':
          daysAgo = 7;
          break;
        case '1-month':
          daysAgo = 30;
          break;
        case '1-year':
          daysAgo = 365;
          break;
      }

      if (daysAgo > 0) {
        const optionStartDate = new Date(now);
        optionStartDate.setDate(optionStartDate.getDate() - daysAgo);

        // Use the most recent start date (smallest daysAgo)
        if (!startDate || optionStartDate > startDate) {
          startDate = optionStartDate;
        }
      }
    });

    if (!startDate) {
      return null;
    }

    // Format dates as ISO strings with timezone offset
    const formatDateWithTimezone = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      // Get timezone offset
      const offset = -date.getTimezoneOffset();
      const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
      const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');
      const offsetSign = offset >= 0 ? '+' : '-';

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}${offsetMinutes}`;
    };

    // At this point, startDate is guaranteed to be non-null due to the check above
    const finalStartDate: Date = startDate;
    finalStartDate.setHours(0, 0, 0, 0);
    // Set end date to end of day
    endDate.setHours(23, 59, 59, 999);

    return {
      start: formatDateWithTimezone(finalStartDate),
      end: formatDateWithTimezone(endDate)
    };
  }

  /**
   * Update pagination from API response paginationInfo
   */
  private updatePaginationFromResponse(response: any): void {
    // Check for paginationInfo at root level
    if (response?.paginationInfo) {
      const paginationInfo = response.paginationInfo;

      // Use totalPages if available, otherwise calculate from totalCount
      if (paginationInfo.totalPages !== undefined) {
        this.totalPages = paginationInfo.totalPages;
      } else if (paginationInfo.totalCount !== undefined) {
        this.totalPages = Math.ceil(paginationInfo.totalCount / this.itemsPerPage);
      }

      // Update currentPage if provided in paginationInfo
      if (paginationInfo.page !== undefined) {
        this.currentPage = paginationInfo.page;
      }
    }
    // Fallback: Check for totalCount in result object
    else if (response?.result?.totalCount !== undefined) {
      this.totalPages = Math.ceil(response.result.totalCount / this.itemsPerPage);
    }
    // Fallback: Check for totalCount at root level
    else if (response?.totalCount !== undefined) {
      this.totalPages = Math.ceil(response.totalCount / this.itemsPerPage);
    }
    // Fallback: Check for totalPages in result object
    else if (response?.result?.totalPages !== undefined) {
      this.totalPages = response.result.totalPages;
    }
    // Fallback: Check for totalPages at root level
    else if (response?.totalPages !== undefined) {
      this.totalPages = response.totalPages;
    }
  }

  /**
   * Load mock usecases as fallback
   */
  private loadMockUsecases(): void {
    this.usecases = [
      {
        id: '1',
        title: 'Invasive Species Prediction & Detection',
        description: 'Helps predict the spread of invasive species within a specific plantation/habitat utilising satellite imagery & ecological data.',
        tags: ['Smart Agri', 'AI Agri', 'Habitat planning', 'Satellite Imagery'],
        badge: 'Public',
        badgeColor: 'public',
        link: '/usecase/1'
      },
      {
        id: '2',
        title: 'Real-time carbon stock potential estimation',
        description: 'Leverages remote sensing & field data to develop a model that can estimate carbon stock potential of a given site',
        tags: ['Carbon Footprint', 'Remote Sensing', 'Carbon Intelligence', 'Satellite Imagery'],
        badge: 'Public',
        badgeColor: 'public',
        link: '/usecase/2'
      }
    ];
    // Update totalPages based on mock data length
    this.totalPages = Math.ceil(this.usecases.length / this.itemsPerPage);
  }

  onFilterChange(filterConfig: FilterConfig): void {
    this.filterConfig = filterConfig;
    this.updateActiveFilterCount();
    this.currentPage = 1; // Reset to first page when filters change
    this.loadUsecases();
  }

  onResetFilters(): void {
    this.filterConfig.sections.forEach(section => {
      if (section.options) {
        section.options.forEach(option => {
          option.checked = false;
        });
      }
    });
    this.updateActiveFilterCount();
    this.loadUsecases();
  }

  onSearchChange(query: string): void {
    const trimmedQuery = query.trim();

    // Only call search API if query has at least 3 characters
    if (trimmedQuery.length >= 3) {
      this.searchQuery = trimmedQuery;
      this.currentPage = 1; // Reset to first page when searching
      this.loadUsecases();
    } else if (trimmedQuery.length === 0) {
      // If search is cleared, reset and load all usecases
      this.searchQuery = '';
      this.currentPage = 1;
      this.loadUsecases();
    }
    // If query is 1-2 characters, don't call API (wait for more input)
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadUsecases();
  }

  onViewModeChange(mode: 'grid' | 'list' | 'table'): void {
    this.viewMode = mode;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
    this.currentPage = 1; // Reset to first page when changing items per page
    this.loadUsecases();
  }

  onSortChange(sortOption: string): void {
    this.sortOption = sortOption;
    this.currentPage = 1;
    this.loadUsecases();
  }

  private getSortStringFromOption(option: string): string {
    switch (option) {
      case 'a-z':
        return 'name:asc;';
      case 'z-a':
        return 'name:desc;';
      case 'new-to-old':
        return 'itemCreatedAt:desc;';
      case 'old-to-new':
        return 'itemCreatedAt:asc;';
      default:
        return 'itemCreatedAt:desc;';
    }
  }

  openMobileFilter(): void {
    this.isMobileFilterOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeMobileFilter(): void {
    this.isMobileFilterOpen = false;
    document.body.style.overflow = '';
  }

  private updateActiveFilterCount(): void {
    let count = 0;
    this.filterConfig.sections.forEach(section => {
      if (section.options) {
        count += section.options.filter(opt => opt.checked).length;
      }
    });
    this.filterConfig.activeFilterCount = count;
  }
}

